import * as React from "react";
import {
  Check,
  ChevronsUpDown,
  GalleryVerticalEnd,
  LogOut,
  Search,
} from "lucide-react";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { sidebarLinks } from "@/config/dashboard";
import { Icons } from "@/components/shared/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ReactNode, useEffect, useState } from "react";
import { useAuth } from "@/AuthContext";
import { Drawer } from "@/components/ui/drawer";
import ContentDrawer from "../Layout/ContentDrawer";
import { DrawerProvider } from "@/context/DrawerContext";
import { LanguageProvider, useLanguage } from "@/context/LanguageContext";
import { useUnsavedChanges } from "@/context/UnsavedChangesContext";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import LanguageSelect from "@/components/shared/languageSelect";

interface ComponentProps {
  children: ReactNode;
}

export default function Component({ children }: ComponentProps) {
  // get acrtive page from url
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();
  const { setLanguage } = useLanguage();
  const { isDirty } = useUnsavedChanges();

  // Guard for client-side exits that never fire `beforeunload` — sidebar
  // `Link` navigation and logout — so they don't silently blow away a dirty
  // screen's staged edits (see UnsavedChangesContext). Holds the action to
  // run if the user confirms; `null` means no confirmation is pending, which
  // also drives the shared ConfirmationDialog's `open` prop below.
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(
    null
  );

  // Wrap a sidebar `Link`'s click: if nothing is dirty, do nothing and let
  // the Link navigate normally (no extra re-render, no behavior change for
  // the common case). If something is dirty, prevent the Link's own
  // navigation and stage it behind the confirmation dialog instead.
  const guardLinkClick = (href: string) => (e: React.MouseEvent) => {
    if (!isDirty()) return;
    e.preventDefault();
    setPendingAction(() => () => navigate(href));
  };

  const handleLogoutClick = () => {
    if (!isDirty()) {
      logout();
      return;
    }
    setPendingAction(() => () => logout());
  };

  interface Page {
    title: string;
    href: string;
    subItems?: Page[];
  }

  const [breadcrumbItems, setBreadcrumbItems] = React.useState<Page[]>([]);

  //console.log(pathname);

  // update activePage and activeSubPage if path changes
  useEffect(() => {
    const activeSection = sidebarLinks.find((section) => {
      const page = section.items.find((page) => {
        return pathname.includes(page.href);
      });
      return page;
    });

    const activePage = activeSection?.items.find((page) => {
      return pathname.includes(page.href);
    });

    const activeSubPage = activePage?.subItems?.find((subPage) => {
      return pathname.includes(subPage.href);
    });

    const breadcrumbItems = [
      {
        title: activeSection?.title || "",
        href: "/",
      },
      {
        title: activePage?.title || "",
        href: activePage?.href || "",
      },
      {
        title: activeSubPage?.title || "",
        href: activeSubPage?.href || "",
      },
    ].filter((item) => item.title);

    setBreadcrumbItems(breadcrumbItems);
  }, [pathname]);

  // <DrawerTrigger>Open</DrawerTrigger>
  // <DrawerContent>
  //   <DrawerHeader>
  //     <DrawerTitle>Are you absolutely sure?</DrawerTitle>
  //     <DrawerDescription>This action cannot be undone.</DrawerDescription>
  //   </DrawerHeader>
  //   <DrawerFooter>
  //     <Button>Submit</Button>
  //     <DrawerClose>
  //       <Button variant="outline">Cancel</Button>
  //     </DrawerFooter>
  // </DrawerContent>

  return (
    <DrawerProvider>
      <SidebarProvider>
        <LanguageProvider>
          <Sidebar>
            <SidebarHeader className="p-2">
              <img src="/Furnisystems.png" alt="Furnisystems" className="p-4" />{" "}
              {/* Use image from public folder */}
              {/* <form>
            <SidebarGroup className="py-0">
              <SidebarGroupContent className="relative">
              <Label htmlFor="search" className="sr-only">
                Search
              </Label>
              <SidebarInput
                id="search"
                placeholder="Search the docs..."
                className="pl-8"
              />
              <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 select-none opacity-50" />
              </SidebarGroupContent>
            </SidebarGroup>
            </form> */}
            </SidebarHeader>
            <SidebarContent>
              {/* We create a SidebarGroup for each parent. */}
              {sidebarLinks.map((item) => (
                <SidebarGroup key={item.title}>
                  <SidebarGroupLabel>{item.title}</SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {item.items.map((item) => {
                        const Icon = item.icon ? Icons[item.icon] : null;
                        return (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton asChild>
                              <a href={item.href}>
                                {" "}
                                {Icon && <Icon />}
                                {item.title}
                              </a>
                            </SidebarMenuButton>
                            <SidebarMenuSub>
                              {item.subItems &&
                                item.subItems
                                  .filter((subItem) => subItem.hidden !== true)
                                  .map((subItem) => {
                                    return (
                                      <SidebarMenuSubItem key={subItem.title}>
                                        <SidebarMenuSubButton asChild>
                                          <Link
                                            to={subItem.href}
                                            onClick={guardLinkClick(
                                              subItem.href
                                            )}
                                          >
                                            {subItem.title}
                                          </Link>
                                        </SidebarMenuSubButton>
                                      </SidebarMenuSubItem>
                                    );
                                  })}
                            </SidebarMenuSub>
                          </SidebarMenuItem>
                        );
                      })}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              ))}
            </SidebarContent>
            <SidebarRail />
            <SidebarFooter>
              <SidebarMenuButton onClick={handleLogoutClick}>
                <LogOut />
                Logout
              </SidebarMenuButton>
            </SidebarFooter>
          </Sidebar>
          <SidebarInset>
            <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4 justify-between">
              <div className="flex items-center gap-2">
                <SidebarTrigger className="-ml-1" />
                <Separator orientation="vertical" className="mr-2 h-4" />
                <Breadcrumb>
                  <BreadcrumbList>
                    {breadcrumbItems.map((item, index) => {
                      return (
                        <React.Fragment key={item.title}>
                          <BreadcrumbItem
                            className="hidden md:block"
                            key={item.title}
                          >
                            <BreadcrumbPage>{item.title}</BreadcrumbPage>
                          </BreadcrumbItem>
                          {index < breadcrumbItems.length - 1 && (
                            <BreadcrumbSeparator
                              className="hidden md:block"
                              key={item.title + "_separator"}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
              <LanguageSelect
                languageOnChange={(language) => {
                  setLanguage(language);
                }}
              />
            </header>
            {children}
          </SidebarInset>
          <ContentDrawer />
        </LanguageProvider>
      </SidebarProvider>
      {/* Same dialog/copy as PageDetails' "Back to Pages" guard — reused here
          for sidebar navigation + logout so the warning looks identical
          regardless of how the user tries to leave a dirty screen. */}
      <ConfirmationDialog
        open={pendingAction !== null}
        title="Discard unsaved changes?"
        description="You have unsaved changes. Leaving this page will discard them."
        confirmText="Discard & leave"
        cancelText="Stay"
        onCancel={() => setPendingAction(null)}
        onConfirm={() => {
          const action = pendingAction;
          setPendingAction(null);
          action?.();
        }}
      />
    </DrawerProvider>
  );
}
